import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  LinearProgress,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import ResponsiveDataGrid from "../../../../../components/MobileList/ResponsiveDataGrid";
import { MdOutlineClear } from "react-icons/md";
import { JReports, setReportSearch } from "../../../../../app/slices/jomla/reportsSlice";
import { restoreFilters } from "../../../../../helpers/restoreFilters";
import DateRangepicker from "../../../../../helpers/dateRangePicker";
import dayjs from "dayjs";
import {
  HiOutlineArrowTrendingDown,
  HiOutlineArrowTrendingUp,
  HiOutlineBanknotes,
  HiOutlineChartBar,
  HiOutlineReceiptPercent,
  HiOutlineShoppingCart,
} from "react-icons/hi2";
import StatusCard from "../../../../home/statistics-card/StatusCard";
import { TONE } from "../../../../../components/MobileList";

const JRevenueTable = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.jReports.jRevenue);
  const loading = useSelector((state) => state.jReports.loading);
  const reportTypes = useSelector((state) => state.jReports.reportTypes);

  const _SearchBody = useSelector((state) => state.jReports.searchBody.revenue);
  const [search, setSearch] = useState(() =>
    restoreFilters({
    startDate: dayjs().format("YYYY-MM-01"),
    endDate: dayjs().format("YYYY-MM-DD"),
  }, _SearchBody)
  );
  useEffect(() => {
    dispatch(setReportSearch({ name: "revenue", body: search }));
  }, [dispatch, search]);

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.2,
    },
    {
      field: "number",
      headerName: "رقم الطلب",
      flex: 0.8,
    },
    {
      field: "pos_number",
      headerName: "POS",
      flex: 0.8,
    },
    {
      field: "pos_name",
      headerName: "اسم النقطة",
      flex: 1.2,
    },
    {
      field: "items_count",
      headerName: "عدد المواد",
      flex: 0.8,
      renderCell: (params) => {
        return (
          <Typography>{params.row.items_count?.toLocaleString()}</Typography>
        );
      },
    },
    {
      field: "created_at",
      headerName: "تاريخ الإنشاء",
      flex: 1,
      renderCell: (params) => {
        return (
          <Stack>
            <Typography>
              {dayjs(params.row.created_at).format("YYYY-MM-DD")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {dayjs(params.row.created_at).format("hh:mm A")}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "revenue",
      headerName: "الربح (د.ع)",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography color={params.row.revenue >= 0 ? "green" : "error"}>
            {params.row.revenue?.toLocaleString()}
          </Typography>
        );
      },
    },
  ];

  useEffect(() => {
    if (reportTypes[7]?.isSelected) {
      dispatch(
        JReports.jRevenue({
          start_date: search.startDate,
          end_date: search.endDate,
        })
      );
    }
  }, [dispatch, reportTypes, search]);

  const customToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport
        csvOptions={{
          fileName: "تقرير الأرباح | نظام دجلة",
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  );

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          {data && (
            <>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {[
                    {
                      name: "عدد الطلبات",
                      value: data.j_orders_count,
                      unit: "طلب",
                      icon: <HiOutlineShoppingCart />,
                      tone: TONE.brand,
                    },
                    {
                      name: "مجموع الطلبات",
                      value: data.j_orders_amount,
                      unit: "د.ع",
                      icon: <HiOutlineBanknotes />,
                      tone: "#7a5af8",
                    },
                    {
                      name: "الخصومات",
                      value: data.j_order_discount,
                      unit: "د.ع",
                      icon: <HiOutlineReceiptPercent />,
                      tone: TONE.warning,
                    },
                    {
                      name: "المصروفات",
                      value: data.j_spending,
                      unit: "د.ع",
                      icon: <HiOutlineArrowTrendingDown />,
                      tone: TONE.danger,
                    },
                    {
                      name: "الإيرادات",
                      value: data.j_orders_revenue,
                      unit: "د.ع",
                      icon: <HiOutlineArrowTrendingUp />,
                      tone: "#0e9384",
                    },
                    {
                      name: "صافي الأرباح",
                      value: data.j_net_revenue,
                      unit: "د.ع",
                      icon: <HiOutlineChartBar />,
                      tone: data.j_net_revenue >= 0 ? TONE.success : TONE.danger,
                    },
                  ].map((item) => (
                    <Grid key={item.name} item xs={6} md={4}>
                      <StatusCard item={item} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={3}>
            <DateRangepicker
              startDate={search.startDate}
              endDate={search.endDate}
              handleOnChange={(searchDate) => {
                const updatedFilter = {
                  from: dayjs(searchDate[0].startDate).format("YYYY-MM-DD"),
                  to: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                };
                if (
                  updatedFilter.from !== searchDate.startDate ||
                  updatedFilter.to !== searchDate.endDate
                ) {
                  setSearch({
                    ...search,
                    startDate: dayjs(searchDate[0].startDate).format(
                      "YYYY-MM-DD"
                    ),
                    endDate: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                  });
                }
              }}
            />
          </Grid>
          <Grid item xs="auto" md={1}>
            <Tooltip title="الغاء الفلتر">
              <IconButton
                onClick={() => {
                  setSearch({
                    startDate: dayjs().format("YYYY-MM-01"),
                    endDate: dayjs().format("YYYY-MM-DD"),
                  });
                }}
              >
                <MdOutlineClear />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <ResponsiveDataGrid gridHeight={null}
              rows={
                data?.j_orders?.map((x, index) => ({
                  ...x,
                  index: index + 1,
                })) ?? []
              }
              autoHeight
              loading={loading}
              columns={columns}
              rowHeight={40}
              components={{
                LoadingOverlay: LinearProgress,
                Toolbar: customToolbar,
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default JRevenueTable;
